import { NestedUser } from "@habity/types";

/**
 * Check if a user has shown interest in a home
 * @param user The user to check
 * @param homeId The home ID to check for
 * @param interestLevel Optional interest level to check for (e.g., 'following_bidding')
 * @returns True if the user has shown interest in the home, false otherwise
 */
export const isAddedToInterest = (user: NestedUser, homeId: number, interestLevel?: string) => {
  const { interests } = user || {};
  if (!interests) return false;
  
  // If interest level is specified, check for that specific level
  if (interestLevel) {
    return interests.some(
      (interest) => 
        interest?.home_id === Number(homeId) && 
        interest?.interest_level === interestLevel
    );
  }
  
  // Otherwise, check for any interest
  return interests.some((interest) => interest?.home_id === Number(homeId));
};

/**
 * Check if a user has shown interest in a home with a level of 'followed_bidding' or higher
 * @param user The user to check
 * @param homeId The home ID to check for
 * @returns True if the user has shown interest in the home, false otherwise
 */
export const hasFollowedBiddingOrHigherInterest = (user: NestedUser, homeId: number) => {
  const { interests } = user || {};
  if (!interests) return false;
  // Check if any interest is 'followed_bidding' or higher
  return interests.some(
    (interest) => 
      interest?.home_id === Number(homeId) && 
      ['followed_bidding', 'paused_bidding', 'placed_bid'].includes(interest?.interest_level || '')
  );
};

/**
 * Check if a user has subscribed to final price for a home
 * @param user The user to check
 * @param homeId The home ID to check for
 * @returns True if the user has subscribed to final price for the home
 */
export const hasSubscribedFinalPrice = (user: NestedUser, homeId: number) => {
  const { interests } = user || {};
  if (!interests) return false;
  return interests.some(
    (interest) =>
      interest?.home_id === Number(homeId) &&
      interest?.interest_level === 'subscribed_final_price'
  );
};

/**
 * Check if a user has shown interest in a project
 * @param user The user to check
 * @param projectId The project ID to check for
 * @param interestLevel Optional interest level to check for (e.g., 'showed_interest')
 * @returns True if the user has shown interest in the project, false otherwise
 */
export const isAddedToProjectInterest = (user: NestedUser, projectId: number, interestLevel?: string) => {
  const { project_interests } = user || {};
  if (!project_interests) return false;
  
  // If interest level is specified, check for that specific level
  if (interestLevel) {
    return project_interests.some(
      (interest) => 
        interest?.project_id === Number(projectId) && 
        interest?.interest_level === interestLevel
    );
  }
  
  // Otherwise, check for any interest
  return project_interests.some((interest) => interest?.project_id === Number(projectId));
};
